"use client";

import { CSSProperties, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";

import { cn } from "@/lib/utils";
import { IEventContentCard, PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { TicketCTA, TicketGeometry, ticketPieceMask } from "@/components/pageComponents/event/EventShared";

/** Ticket-piece geometry for the numbered tab strip (325×53 in Figma). */
const STRIP_GEOMETRY: TicketGeometry = {
  notchR: "0.95rem",
  notchOffset: "0.4rem",
  outline: { band: "0.33rem", line: "0.12rem" },
  dashes: { width: "0.12rem", length: "0.22rem", period: "0.44rem", top: "0.95rem", height: "4.75rem" },
};
const STRIP_MASKS: Record<string, CSSProperties> = {
  first: ticketPieceMask(["right"], STRIP_GEOMETRY),
  middle: ticketPieceMask(["left", "right"], STRIP_GEOMETRY),
  last: ticketPieceMask(["left"], STRIP_GEOMETRY),
  solo: ticketPieceMask([], STRIP_GEOMETRY),
};

/** Strip layout in rem: total width, fixed (inactive) cell width, tear gap beside the active cell. */
const STRIP_W = 42;
const STRIP_CELL_W = 6;
const STRIP_GAP = 0.9;
const STRIP_SPRING = { type: "spring", stiffness: 210, damping: 28, mass: 0.8 } as const;

/**
 * Numbered ticket tab strip built from real ticket pieces. The active cell
 * carries the label and tears itself off the strip (gap).
 *
 * Width / margins are animated as real CSS values (not `layout`
 * transforms) so the gradient mask keeps its notch shape while resizing.
 */
function CardTabStrip({
  index,
  label,
  count = 3,
  onSelect,
}: {
  index: number;
  label?: string;
  count?: number;
  onSelect?: (index: number) => void;
}) {
  const cells = Array.from({ length: count }, (_, i) => i);
  const gapL = index > 0 ? STRIP_GAP : 0;
  const gapR = index < count - 1 ? STRIP_GAP : 0;
  const activeW = STRIP_W - (count - 1) * STRIP_CELL_W - gapL - gapR;

  return (
    <div className="relative flex h-[7.125rem] w-[42rem] shrink-0 items-stretch pt-[0.5rem]">
      {cells.map((cell) => {
        const mask = count === 1 ? "solo" : cell === 0 ? "first" : cell === count - 1 ? "last" : "middle";
        const isActive = cell === index;
        return (
          <motion.button
            key={cell}
            type="button"
            aria-pressed={isActive}
            aria-label={`0${cell + 1}`}
            onClick={onSelect ? () => onSelect(cell) : undefined}
            style={STRIP_MASKS[mask]}
            initial={false}
            animate={{
              width: `${isActive ? activeW : STRIP_CELL_W}rem`,
              marginLeft: `${isActive ? gapL : 0}rem`,
              marginRight: `${isActive ? gapR : 0}rem`,
            }}
            transition={STRIP_SPRING}
            className={cn(
              "relative flex shrink-0 items-center justify-center overflow-hidden bg-[#ffda74] text-black",
              onSelect ? "cursor-pointer" : "cursor-default",
              isActive && "z-10",
            )}>
            {/* Content crossfades while the piece itself resizes */}
            <AnimatePresence
              initial={false}
              mode="wait">
              <motion.span
                key={isActive ? `label-${label ?? ""}` : "number"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="absolute inset-0 flex items-center justify-center">
                {isActive ? (
                  <div className="flex max-w-full items-center gap-[0.75rem] px-[1.25rem]">
                    <span className="shrink-0 font-blacker text-[2.5rem] leading-none italic">{`0${cell + 1}`}</span>
                    <span className="min-w-0 truncate font-geist text-[1.5rem] leading-[2rem] font-medium tracking-[-0.02rem] whitespace-nowrap uppercase">
                      {label ?? `0${cell + 1}`}
                    </span>
                  </div>
                ) : (
                  <span className="pt-[0.5rem] text-center font-blacker text-[2.72rem] leading-none italic">
                    {`0${cell + 1}`}
                  </span>
                )}
              </motion.span>
            </AnimatePresence>
          </motion.button>
        );
      })}
    </div>
  );
}

function CardVisualMedia({ card }: { card: IEventContentCard }) {
  if (card.visualVideo?.src) {
    return (
      <video
        src={card.visualVideo.src}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={card.visual?.src}
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }
  if (card.visual?.src) {
    // eslint-disable-next-line @next/next/no-img-element
    return (
      <img
        src={card.visual.src}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }
  return null;
}

const LABEL_MASK = ticketPieceMask([], { ...STRIP_GEOMETRY, outline: { band: "0.25rem", line: "0.089rem" } });

function CardTicketLabel({ index, label }: { index: number; label?: string }) {
  return (
    <div
      style={LABEL_MASK}
      className="flex items-center self-start bg-[#ffda74] px-[2.5rem] py-[1.625rem] text-black">
      <span className="font-geist text-[1.75rem] leading-[1.92rem] font-medium whitespace-nowrap uppercase">
        {index + 1}. {label}
      </span>
    </div>
  );
}

function EventContentCard({ card, index }: { card: IEventContentCard; index: number }) {
  return (
    <div className="relative w-[min(46.667rem,100%)] overflow-clip border-[0.112rem] border-black/10 bg-white lg:h-[64.375rem] lg:w-[150rem]">
      <div className="flex flex-col gap-[4rem] px-[2rem] py-[2rem] lg:block lg:p-0">
        <div className="relative aspect-[336/310] w-full overflow-clip bg-[#f5f5f5] lg:absolute lg:top-1/2 lg:left-[73.875rem] lg:h-[60.375rem] lg:w-[74.25rem] lg:-translate-y-1/2">
          <CardVisualMedia card={card} />
        </div>
        <div className="flex flex-col gap-[4rem] lg:absolute lg:top-[4rem] lg:left-[3.25rem] lg:w-[42rem] lg:gap-[14.25rem]">
          <CardTicketLabel
            index={index}
            label={card.tabLabel}
          />
          <div className="flex flex-col gap-[2.5rem]">
            {card.title && (
              <p className="mb-0 w-[28rem] font-geist text-[3rem] leading-[1.1] font-normal tracking-[-0.09rem] text-black lg:w-[36.125rem] lg:text-[4rem] lg:tracking-[-0.12rem]">
                {card.title}
              </p>
            )}
            {(card.description || card.descriptionStrong) && (
              <p className="mb-0 w-full font-geist text-[2rem] leading-[1.5] font-normal tracking-[-0.06rem] whitespace-pre-line lg:w-[39.125rem]">
                {card.description && <span className="text-black/70">{card.description} </span>}
                {card.descriptionStrong && <span className="text-black">{card.descriptionStrong}</span>}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Badge + title + description of the Event Contents section. */
function EventContentsHeader({ data }: { data: PageEventSchema }) {
  return (
    <div className="flex w-full flex-col items-center gap-[2.75rem]">
      {data.s2BadgeText && (
        <div className="flex items-center gap-[1.25rem] border border-black/10 bg-black/[0.04] px-[2.5rem] py-[1.5rem]">
          <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
          <p className="mb-0 font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-black uppercase">
            {data.s2BadgeText}
          </p>
        </div>
      )}
      {(data.s2TitleItalic || data.s2TitleRest) && (
        <h2 className="mb-0 w-[41rem] text-center font-blacker text-[4.5rem] leading-[1.12] font-normal tracking-[-0.135rem] text-black lg:w-[74.75rem] lg:text-[6rem] lg:tracking-[-0.18rem] [&_span]:font-blacker">
          {data.s2TitleItalic && <span className="text-[#001b14] italic">{data.s2TitleItalic} </span>}
          {data.s2TitleRest}
        </h2>
      )}
      {(data.s2DescriptionMuted || data.s2DescriptionStrong) && (
        <p className="mb-0 w-[45.75rem] text-center font-geist text-[2.25rem] leading-[1.5] font-normal lg:w-[63.875rem]">
          {data.s2DescriptionMuted && <span className="text-black/70">{data.s2DescriptionMuted} </span>}
          {data.s2DescriptionStrong && <span className="text-black">{data.s2DescriptionStrong}</span>}
        </p>
      )}
    </div>
  );
}

/**
 * Desktop scrollytelling: header + one fixed card pinned full-screen; scrolling
 * through the tall wrapper swaps which card's content is shown.
 */
function EventCardsScrolly({ data, cards }: { data: PageEventSchema; cards: IEventContentCard[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const n = cards.length;
    if (!n) return;
    setActive(Math.max(0, Math.min(n - 1, Math.floor(v * n * 0.999))));
  });

  /** Scroll so progress lands mid-way through card `i`'s range (mirrors the mapping above). */
  const scrollToCard = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const n = cards.length;
    const rect = el.getBoundingClientRect();
    const range = rect.height - window.innerHeight;
    const top = window.scrollY + rect.top + ((i + 0.5) / n) * range;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div
      ref={ref}
      style={{ height: `${Math.max(1, cards.length) * 110}vh` }}
      className="relative w-full">
      <div className="sticky top-0 flex h-screen w-full flex-col items-center justify-center gap-[6rem]">
        <EventContentsHeader data={data} />
        {/* Fixed card shell — only the content inside swaps */}
        <div className="relative h-[64.375rem] w-[150rem] overflow-clip border-[0.112rem] border-black/10 bg-white">
          {/* Persistent ticket tab strip = step indicator */}
          <div className="absolute top-[4rem] left-[3.25rem] z-10 w-[42rem]">
            <CardTabStrip
              index={active}
              label={cards[active]?.tabLabel}
              count={cards.length}
              onSelect={scrollToCard}
            />
          </div>
          {/* Animated text */}
          <div className="absolute top-[24.875rem] left-[3.25rem] w-[42rem]">
            <AnimatePresence
              mode="wait"
              initial={false}>
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="flex flex-col gap-[2.5rem]">
                {cards[active]?.title && (
                  <p className="mb-0 w-[36.125rem] font-geist text-[4rem] leading-[1.1] font-normal tracking-[-0.12rem] text-black">
                    {cards[active].title}
                  </p>
                )}
                {(cards[active]?.description || cards[active]?.descriptionStrong) && (
                  <p className="mb-0 w-[39.125rem] font-geist text-[2rem] leading-[1.5] font-normal tracking-[-0.06rem] whitespace-pre-line lg:pr-[2rem]">
                    {cards[active]?.description && <span className="text-black/70">{cards[active].description} </span>}
                    {cards[active]?.descriptionStrong && (
                      <span className="text-black">{cards[active].descriptionStrong}</span>
                    )}
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          {/* Fixed visual frame, media crossfades inside */}
          <div className="absolute top-1/2 left-[73.875rem] h-[60.375rem] w-[74.25rem] -translate-y-1/2 overflow-clip bg-[#f5f5f5]">
            <AnimatePresence initial={false}>
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="absolute inset-0">
                <CardVisualMedia card={cards[active]} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Event contents section: stacked cards on mobile, pinned scrollytelling card on desktop. */
export function EventContents({ data }: { data: PageEventSchema }) {
  const cards = (data.s2Cards ?? []).slice(0, 3);

  return (
    <section className="w-full bg-white">
      {/* Mobile: header + stacked cards */}
      <div className="flex w-full flex-col items-center gap-[3rem] px-[2rem] py-[8rem] lg:hidden">
        <EventContentsHeader data={data} />
        {!!cards.length && (
          <div className="flex w-full flex-col items-center gap-[5rem]">
            {cards.map((card, i) => (
              <EventContentCard
                key={card._key ?? i}
                card={card}
                index={i}
              />
            ))}
          </div>
        )}
        {/* Mobile-only CTA below the cards (desktop scrolly has none) */}
        <TicketCTA
          data={data.s2Cta ?? data.cta}
          className="w-[min(47.625rem,100%)]"
        />
      </div>
      {/* Desktop: pinned card, content swaps while scrolling */}
      <div className="hidden w-full lg:block">
        {cards.length ? (
          <EventCardsScrolly
            data={data}
            cards={cards}
          />
        ) : (
          <div className="flex w-full flex-col items-center py-[15rem]">
            <EventContentsHeader data={data} />
          </div>
        )}
      </div>
    </section>
  );
}
